import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../utils/i18n';

const HeroAbout = ({ title, subtitle, buttonText, buttonLink, cta = true }) => {
  const { language } = useLanguage();

  return (
    <div className="relative py-24 md:py-32 lg:py-40 overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,69,19,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(139,69,19,0.05)_1px,transparent_1px),linear-gradient(-45deg,rgba(139,69,19,0.05)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 container-custom">
        <div className="max-w-5xl mx-auto text-center">
          
          {/* Decorative line */}
          <motion.div 
            className="w-24 h-0.5 bg-gradient-to-r from-transparent via-akasha-brown to-transparent mx-auto mb-8"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          />

          {/* Title with enhanced typography */}
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-akasha-brown mb-8 font-serif tracking-wide"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <span className="relative inline-block">
              {title}
              {/* Subtle underline accent */}
              <motion.div 
                className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-akasha-brown/30 to-akasha-brown/10 rounded-full"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              />
            </span>
          </motion.h1>

          {subtitle && (
            <motion.div
              className="max-w-4xl mx-auto mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <p className="text-xl sm:text-2xl md:text-3xl text-akasha-black/70 font-light leading-relaxed tracking-wide">
                {subtitle}
              </p>
            </motion.div>
          )}

          {/* Decorative elements */}
          <motion.div 
            className="flex justify-center items-center space-x-6 mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <div className="w-2 h-2 bg-akasha-brown rounded-full"></div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-akasha-brown/50 to-transparent"></div>
            <div className="w-3 h-3 border border-akasha-brown/50 rounded-full"></div>
            <div className="w-16 h-0.5 bg-gradient-to-l from-akasha-brown/50 to-transparent"></div>
            <div className="w-2 h-2 bg-akasha-brown rounded-full"></div>
          </motion.div>

          {cta && buttonText && buttonLink && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <Link 
                to={buttonLink} 
                className="group relative inline-flex items-center gap-3 text-akasha-brown text-lg font-medium tracking-wide hover:text-akasha-brown/80 transition-colors duration-300"
              >
                <span className="relative z-10 px-8 py-4 border-2 border-akasha-brown rounded-full hover:bg-akasha-brown hover:text-white transition-all duration-300">
                  {buttonText}
                </span>
                <div className="w-8 h-8 bg-akasha-brown/10 rounded-full flex items-center justify-center group-hover:bg-akasha-brown group-hover:text-white transition-all duration-300">
                  <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            </motion.div>
          )}
        </div>

        {/* Enhanced floating decorative elements */}
        <motion.div 
          className="absolute top-1/4 left-10 w-8 h-8 border-2 border-akasha-brown/20 rounded-full hidden lg:block"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          <div className="w-2 h-2 bg-akasha-brown/30 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
        </motion.div>
        
        <motion.div 
          className="absolute top-1/3 right-16 w-6 h-6 bg-gradient-to-br from-akasha-brown/20 to-akasha-brown/10 rounded-full hidden lg:block shadow-lg"
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <motion.div 
          className="absolute bottom-1/4 left-1/4 w-10 h-10 border-2 border-akasha-brown/15 rounded-xl hidden lg:block"
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        >
          <div className="w-full h-full bg-gradient-to-br from-akasha-brown/5 to-transparent rounded-lg"></div>
        </motion.div>

        {/* Additional sophisticated elements */}
        <motion.div 
          className="absolute top-16 right-1/3 w-3 h-12 bg-gradient-to-b from-akasha-brown/20 to-transparent rounded-full hidden lg:block"
          animate={{ scaleY: [1, 1.2, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <motion.div 
          className="absolute bottom-16 right-20 w-5 h-5 border border-akasha-brown/25 rounded-lg hidden lg:block"
          animate={{ rotate: [0, 45, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-1 h-1 bg-akasha-brown/40 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
        </motion.div>
      </div>
    </div>
  );
};

export default HeroAbout; 